import React from 'react';
import { Link } from 'react-router-dom';
import V2SEO from '../components/V2SEO';
import LazyVideo from '../components/LazyVideo';
import { business, equipment, serviceAreas } from '../data/realContent';

const About = () => (
  <>
    <V2SEO
      title="About — Josh Kolonia, FAA-Certified Drone Pilot in Bethlehem, PA"
      description="Cosmik Drones is operated by Josh Kolonia, an FAA Part 107 certified drone pilot based in Bethlehem, PA. Member of the Greater Lehigh Valley Chamber of Commerce, flying for MLB, FanDuel, Home Depot, and more."
      path="/about"
      keywords="Josh Kolonia, Cosmik Drones, FAA Part 107 drone pilot Pennsylvania, Lehigh Valley drone operator, Bethlehem PA drone pilot"
    />

    <section className="v2-svc-hero">
      <div className="v2-container">
        <div className="v2-split v2-split--wide-media" style={{ alignItems: 'start' }}>
          <div>
            <span className="v2-eyebrow">About Cosmik Drones</span>
            <h1>Professional Aerial Cinematography, Led by an FAA-Certified Pilot</h1>
            <p className="v2-prose" style={{ marginTop: '1.2rem' }}>
              Cosmik Drones is operated by {business.owner}, an FAA Part 107 certified drone
              pilot based in {business.city}, {business.region}. The company specializes in
              aerial cinematography, FPV drone filming, and aerial photography for commercial
              productions, live events, real estate, and brand campaigns throughout the
              Lehigh Valley and the East Coast.
            </p>
            <p className="v2-prose" style={{ marginTop: '1rem' }}>
              A member of the {business.chamberMember}, Cosmik Drones has delivered work for
              clients including MLB, the Lehigh Valley Phantoms, Reading Royals, FanDuel,
              The Home Depot, Sam's Club, Four Seasons, Beats by Dre, Penn Museum, Union League,
              and Skirmish, as well as the Pennsylvania Commonwealth's "PA Gets It Done"
              campaign with Red House Communications and JTWO.
            </p>
            <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap', marginTop: '1.6rem' }}>
              <Link to="/contact" className="v2-btn v2-btn--primary">Request a Quote</Link>
              <a href={business.chamberUrl} target="_blank" rel="noreferrer" className="v2-btn v2-btn--ghost">Chamber of Commerce Listing</a>
            </div>
          </div>
          <div className="v2-split__media" style={{ aspectRatio: '4 / 5' }}>
            <LazyVideo
              src="/videos/Josh Hard at Work.mp4"
              poster="/images/Josh Hard at Work - frame at 0m7s.jpg"
            />
          </div>
        </div>
      </div>
    </section>

    <section className="v2-section">
      <div className="v2-container">
        <div className="v2-shead">
          <div>
            <span className="v2-eyebrow">Credentials</span>
            <h2>Certifications & Insurance</h2>
          </div>
        </div>
        <div className="v2-trust">
          <div className="v2-trust__item">
            <strong>FAA Part 107</strong>
            <span>Commercial drone certification — current and on file. Image of the license available on request.</span>
          </div>
          <div className="v2-trust__item">
            <strong>Insurance</strong>
            <span>Liability insurance for drone operations; certificate of insurance provided to clients and venues on request.</span>
          </div>
          <div className="v2-trust__item">
            <strong>Chamber member</strong>
            <span>{business.chamberMember}.</span>
          </div>
          <div className="v2-trust__item">
            <strong>Based in {business.city}</strong>
            <span>Operating throughout the Lehigh Valley, Pennsylvania, and the East Coast.</span>
          </div>
        </div>
      </div>
    </section>

    <section className="v2-section">
      <div className="v2-container">
        <div className="v2-shead">
          <div>
            <span className="v2-eyebrow">Equipment</span>
            <h2>Professional Equipment for Every Project</h2>
          </div>
          <p>Each shoot is matched to the right aircraft: large-sensor camera drones for flagship aerial work, ducted cinewhoops for safe interior flight, and freestyle FPV rigs for high-speed action.</p>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '1.2rem',
        }}>
          {equipment.map((e) => (
            <div key={e.name} style={{
              border: '1px solid var(--v2-line)',
              borderRadius: '22px',
              overflow: 'hidden',
              background: 'rgba(255,255,255,0.02)',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <div style={{ aspectRatio: '4 / 3', overflow: 'hidden', background: '#0a0d14' }}>
                <img
                  src={e.image}
                  alt={e.name}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div style={{ padding: '1.4rem' }}>
                <span className="v2-eyebrow">{e.role}</span>
                <h4 style={{ margin: '0.5rem 0 0.4rem', fontSize: '1.15rem' }}>{e.name}</h4>
                <p style={{ margin: 0, color: 'var(--v2-text-dim)', fontSize: '0.92rem' }}>{e.notes}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="v2-section">
      <div className="v2-container">
        <div className="v2-shead">
          <div>
            <span className="v2-eyebrow">Coverage</span>
            <h2>Service Areas</h2>
          </div>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.2rem',
        }}>
          {serviceAreas.map((a) => (
            <div key={a.name} style={{
              border: '1px solid var(--v2-line)',
              borderRadius: '22px',
              padding: '1.8rem',
              background: 'rgba(255,255,255,0.02)',
            }}>
              <h3 style={{ margin: '0 0 0.6rem', fontSize: '1.2rem' }}>{a.name}</h3>
              <p style={{ margin: 0, color: 'var(--v2-text-dim)', lineHeight: 1.65 }}>
                {a.cities.join(' · ')}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="v2-section v2-section--tight">
      <div className="v2-container">
        <div className="v2-cta">
          <h2>Have a project in mind?</h2>
          <p>Most inquiries receive a response within one business day.</p>
          <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap' }}>
            <Link to="/contact" className="v2-btn v2-btn--primary">Request a Quote</Link>
            <a href={business.emailHref} className="v2-btn v2-btn--ghost">{business.email}</a>
          </div>
        </div>
      </div>
    </section>
  </>
);

export default About;
